export type Surface = { bg: string; elev: string; deep: string; ink: string };

export type Inks = { 1: string; 2: string; 3: string; 4: string };

export type Lines = { line: string; soft: string; strong: string; hair: string };

export type Tone = {
  base: string;
  deep: string;
  soft: string;
  tint: string;
  glow: string;
  mid: string;
  bright: string;
  line: string;
  ink: string;
};

export type ToneName = 'halo' | 'aurora' | 'solar' | 'mist' | 'lilac';

export type ToneMap = Record<ToneName, Tone>;

export type Fx = {
  overlay: string;
  glassNav: string;
  insetHi: string;
  insetHiSoft: string;
  insetLo: string;
  insetRing: string;
  insetMark: string;
  ambHalo: string;
  ambHaloSoft: string;
  ambHaloXSoft: string;
  ambAurora: string;
  ringHalo: string;
  ringAurora: string;
  glowHalo: string;
  glowHaloDeep: string;
  chromeDot: string;
  deviceFrame: string;
};

const lightSurface: Surface = { bg: '#FBFBFD', elev: '#FFFFFF', deep: '#F2F2F5', ink: '#0A0A0C' };
const lightInk: Inks = { 1: '#0A0A0C', 2: '#3A3A40', 3: '#76767E', 4: '#AEAEB6' };
const lightLine: Lines = {
  line: '#E5E5EA',
  soft: '#F0F0F4',
  strong: '#1C1C1F',
  hair: 'rgba(10, 10, 12, 0.06)',
};

const lightTone: ToneMap = {
  halo: {
    base: '#818CF8',
    deep: '#6366F1',
    soft: '#E0E7FF',
    tint: '#EEF2FF',
    glow: '#C7D2FE',
    mid: '#A5B4FC',
    bright: '#B4C0FE',
    line: '#C7D2FE',
    ink: '#4338CA',
  },
  aurora: {
    base: '#6EE7B7',
    deep: '#10B981',
    soft: '#D1FAE5',
    tint: '#ECFDF5',
    glow: '#A7F3D0',
    mid: '#34D399',
    bright: '#A7F3D0',
    line: '#A7F3D0',
    ink: '#047857',
  },
  solar: {
    base: '#FCD34D',
    deep: '#F59E0B',
    soft: '#FEF3C7',
    tint: '#FFFBEB',
    glow: '#FDE68A',
    mid: '#FBBF24',
    bright: '#FDE68A',
    line: '#FDE68A',
    ink: '#92400E',
  },
  mist: {
    base: '#7DD3FC',
    deep: '#0EA5E9',
    soft: '#E0F2FE',
    tint: '#F0F9FF',
    glow: '#BAE6FD',
    mid: '#38BDF8',
    bright: '#BAE6FD',
    line: '#BAE6FD',
    ink: '#075985',
  },
  lilac: {
    base: '#C4B5FD',
    deep: '#A78BFA',
    soft: '#EDE9FE',
    tint: '#F5F3FF',
    glow: '#DDD6FE',
    mid: '#C4B5FD',
    bright: '#DDD6FE',
    line: '#DDD6FE',
    ink: '#6D28D9',
  },
};

const lightFx: Fx = {
  overlay: 'rgba(10, 10, 12, 0.45)',
  glassNav: 'rgba(251, 251, 253, 0.78)',
  insetHi: 'rgba(255, 255, 255, 0.3)',
  insetHiSoft: 'rgba(255, 255, 255, 0.35)',
  insetLo: 'rgba(0, 0, 0, 0.16)',
  insetRing: 'rgba(0, 0, 0, 0.06)',
  insetMark: 'rgba(0, 0, 0, 0.05)',
  ambHalo: 'rgba(99, 102, 241, 0.08)',
  ambHaloSoft: 'rgba(99, 102, 241, 0.06)',
  ambHaloXSoft: 'rgba(99, 102, 241, 0.04)',
  ambAurora: 'rgba(110, 231, 183, 0.08)',
  ringHalo: 'rgba(99, 102, 241, 0.16)',
  ringAurora: 'rgba(110, 231, 183, 0.22)',
  glowHalo: 'rgba(129, 140, 248, 0.4)',
  glowHaloDeep: 'rgba(129, 140, 248, 0.5)',
  chromeDot: '#D1D1D6',
  deviceFrame: '#0A0A0C',
};

const darkSurface: Surface = { bg: '#0E0F12', elev: '#181A1F', deep: '#08090B', ink: '#F5F5F7' };
const darkInk: Inks = { 1: '#F5F5F7', 2: '#C7C7CC', 3: '#8E8E93', 4: '#5A5A60' };
const darkLine: Lines = {
  line: '#26272D',
  soft: '#1C1D22',
  strong: '#E5E5EA',
  hair: 'rgba(245, 245, 247, 0.06)',
};

const darkTone: ToneMap = {
  halo: {
    base: '#A5B4FC',
    deep: '#818CF8',
    soft: '#1E1E3F',
    tint: '#161635',
    glow: '#C7D2FE',
    mid: '#818CF8',
    bright: '#B4C0FE',
    line: '#2E2E5A',
    ink: '#C7D2FE',
  },
  aurora: {
    base: '#A7F3D0',
    deep: '#34D399',
    soft: '#052E20',
    tint: '#031F15',
    glow: '#A7F3D0',
    mid: '#34D399',
    bright: '#A7F3D0',
    line: '#1F5240',
    ink: '#A7F3D0',
  },
  solar: {
    base: '#FDE68A',
    deep: '#FBBF24',
    soft: '#2A1F00',
    tint: '#1B1400',
    glow: '#FDE68A',
    mid: '#FBBF24',
    bright: '#FDE68A',
    line: '#4D3A00',
    ink: '#FDE68A',
  },
  mist: {
    base: '#BAE6FD',
    deep: '#38BDF8',
    soft: '#0A2435',
    tint: '#06182A',
    glow: '#BAE6FD',
    mid: '#38BDF8',
    bright: '#BAE6FD',
    line: '#1E4258',
    ink: '#BAE6FD',
  },
  lilac: {
    base: '#DDD6FE',
    deep: '#A78BFA',
    soft: '#1E1A3D',
    tint: '#15132E',
    glow: '#DDD6FE',
    mid: '#C4B5FD',
    bright: '#DDD6FE',
    line: '#3A2F6E',
    ink: '#DDD6FE',
  },
};

const darkFx: Fx = {
  overlay: 'rgba(0, 0, 0, 0.55)',
  glassNav: 'rgba(14, 15, 18, 0.72)',
  insetHi: 'rgba(255, 255, 255, 0.08)',
  insetHiSoft: 'rgba(255, 255, 255, 0.1)',
  insetLo: 'rgba(0, 0, 0, 0.3)',
  insetRing: 'rgba(255, 255, 255, 0.04)',
  insetMark: 'rgba(0, 0, 0, 0.25)',
  ambHalo: 'rgba(165, 180, 252, 0.14)',
  ambHaloSoft: 'rgba(165, 180, 252, 0.1)',
  ambHaloXSoft: 'rgba(165, 180, 252, 0.07)',
  ambAurora: 'rgba(110, 231, 183, 0.1)',
  ringHalo: 'rgba(165, 180, 252, 0.2)',
  ringAurora: 'rgba(110, 231, 183, 0.22)',
  glowHalo: 'rgba(165, 180, 252, 0.45)',
  glowHaloDeep: 'rgba(165, 180, 252, 0.55)',
  chromeDot: '#2A2B30',
  deviceFrame: '#0A0A0C',
};

export const lightTokens = {
  surface: lightSurface,
  ink: lightInk,
  line: lightLine,
  tone: lightTone,
  fx: lightFx,
};
export const darkTokens = { surface: darkSurface, ink: darkInk, line: darkLine, tone: darkTone, fx: darkFx };

export type TokenSet = typeof lightTokens;
